<div class="container-fluid px-2 px-md-3 px-xl-1 py-3">
  <div class="d-flex flex-column gap-3">
    <section class="rounded-4 overflow-hidden entity-config-top-hero text-white shadow-sm">
      <div class="px-3 py-3">
        <div class="row g-2 align-items-start flex-nowrap">
          <div class="col">
            <p class="h4 fw-bold lh-sm mb-1">Service Layer Trigger</p>
            <p class="text-dark-50 mb-0 small">Create, review, and update triggers for the selected SLA.</p>
          </div>

          <div class="col-auto d-flex justify-content-end">
            <button type="button" class="btn btn-light btn-sm rounded-3 px-3 py-2 me-2" @click="go_back">
              Back
            </button>
          </div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="entity-inventory-panel rounded-4 border shadow-sm p-4 text-center text-secondary">
      <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
      Loading SLA details...
    </section>

    <div v-else class="d-flex flex-column gap-3">
      <section v-if="selected_sla" class="rounded-4 border bg-white shadow-sm p-3">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
          <div>
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <p class="fw-bold mb-0">{{ selected_sla.name }}</p>
              <span class="badge rounded-pill text-uppercase" :class="selected_sla.is_active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'">
                {{ selected_sla.status_label }}
              </span>
              <span v-if="selected_sla.workflow_id" class="badge rounded-pill bg-light text-dark border text-uppercase">
                Workflow: {{ selected_sla.workflow_name || selected_sla.workflow_id }}
              </span>
              <span v-else class="badge rounded-pill bg-light text-dark border text-uppercase">
                No workflow assigned
              </span>
            </div>
            <div class="text-secondary small mt-1">{{ selected_sla.description || 'No description provided.' }}</div>
          </div>
        </div>

        <div class="row g-3 mt-2">
          <div class="col-12 col-md-4">
            <div class="rounded-4 border-start border-4 border-primary bg-white shadow p-3 h-100">
              <div class="text-x fw-bold text-secondary mb-2">Target Duration</div>
              <div class="fw-bold text-s lh-1 mb-0">{{ format_duration(selected_sla.target_duration) }}</div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="rounded-4 border-start border-4 border-warning bg-white shadow p-3 h-100">
              <div class="text-x fw-bold text-secondary mb-2">Warning Threshold</div>
              <div class="fw-bold text-s lh-1 mb-0">{{ format_duration(selected_sla.warning_threshold) }}</div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="rounded-4 border-start border-4 border-danger bg-white shadow p-3 h-100">
              <div class="text-x fw-bold text-secondary mb-2">Escalation Duration</div>
              <div class="fw-bold text-s lh-1 mb-0">{{ format_duration(selected_sla.escalation_duration) }}</div>
            </div>
          </div>
        </div>

        <div class="row g-3 mt-3">
          <div class="col-12 col-xl-7">
            <div class="rounded-4 border bg-white shadow-sm p-3 h-100 border-purple border-opacity-50">
              <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <div class="fw-bold text-x">Triggers</div>
                  <div class="text-secondary text-xs">Created triggers for this SLA.</div>
                </div>
                <span class="badge rounded-pill bg-primary-subtle text-primary">{{ sla_triggers.length }} trigger(s)</span>
              </div>

              <div v-if="trigger_loading" class="d-flex align-items-center gap-2 text-secondary text-xs py-2">
                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                <span>Loading triggers...</span>
              </div>

              <div v-else-if="sla_triggers.length" class="d-flex flex-column gap-2">
                <article v-for="trigger in sla_triggers" :key="trigger.id" class="rounded-4 border bg-white p-3 shadow">
                  <div class="d-flex align-items-start justify-content-between gap-3">
                    <div class="d-flex align-items-start gap-3">
                      <div class="entity-type-icon entity-icon-surface rounded-3 d-inline-flex align-items-center justify-content-center">
                        <i v-if="trigger.trigger_type === 'form_submission'" class="bi bi-file-earmark-text text-white fs-5"></i>
                        <i v-else-if="trigger.trigger_type === 'question_answered'" class="bi bi-chat-square-dots text-white fs-5"></i>
                        <i v-else class="bi bi-arrow-left-right text-white fs-5"></i>
                      </div>

                      <div>
                        <div class="fw-bold text-xs mb-1 text-capitalize">
                          {{ String(trigger.trigger_type || '').replace(/_/g, ' ') }}
                        </div>
                        <div v-if="trigger.trigger_type === 'status_change'" class="text-secondary text-xxs">
                          Workflow Status: {{ trigger.workflow_status_name || trigger.workflow_status_id || '-' }}
                        </div>
                        <div v-else-if="trigger.trigger_type === 'question_answered'" class="text-secondary text-xxs">
                          Question: {{ trigger.section_question_text || trigger.section_question_id || '-' }}
                        </div>
                        <div class="text-secondary text-xxs">
                          <i class="bi bi-clock me-1"></i>
                          Auto Start: {{ trigger.trigger_condition?.auto_start ? 'Yes' : 'No' }}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="btn btn-primary-dark btn-sm rounded-3 text-xs px-3"
                      @click="edit_trigger(trigger)"
                    >
                      Edit
                    </button>
                  </div>
                </article>
              </div>

              <div v-else class="text-secondary text-xs bg-light rounded-4 px-3 py-4 text-center">
                No triggers have been created for this SLA yet.
              </div>
            </div>
          </div>

          <div class="col-12 col-xl-5">
            <form id="sla_trigger_form" class="rounded-4 border bg-white shadow-sm p-3 h-100 border-purple border-opacity-50" @submit.prevent="save_trigger">
              <div class="d-flex align-items-start gap-3 mb-3">
                <div class="entity-type-icon entity-icon-surface rounded-3 d-inline-flex align-items-center justify-content-center">
                  <i class="bi bi-plus-lg text-white fs-5"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold text-x">{{ trigger_form.id ? 'Edit Trigger' : 'Create Trigger' }}</div>
                  <div class="text-secondary text-xs">Save a new SLA trigger or update an existing one.</div>
                </div>
              </div>

              <div class="row g-3">
                <div class="col-12">
                  <label class="modal-form-label text-xs" for="sla_trigger_type">Trigger Type</label>
                  <select
                    id="sla_trigger_type"
                    v-model="trigger_form.trigger_type"
                    class="form-select bg-light border-0 rounded-3 text-xs"
                    @change="handle_trigger_type_change"
                  >
                    <option value="status_change">Status Change</option>
                    <option value="question_answered">Question Answered</option>
                    <option value="form_submission">Form Submission</option>
                  </select>
                </div>

                <div v-if="trigger_form.trigger_type === 'status_change'" class="col-12">
                  <label class="modal-form-label text-xs" for="sla_workflow_status_id">Workflow Status</label>
                  <select
                    id="sla_workflow_status_id"
                    v-model="trigger_form.workflow_status_id"
                    class="form-select bg-light border-0 rounded-3 text-xs"
                    :disabled="context_loading || !selected_sla?.workflow_id || !workflow_status_options.length"
                  >
                    <option value="">Select a workflow status</option>
                    <option v-for="status in workflow_status_options" :key="status.id" :value="String(status.id)">
                      {{ status.name }}
                    </option>
                  </select>
                  <div v-if="!context_loading && !workflow_status_options.length" class="text-secondary text-xxs mt-1">
                    No workflow statuses are available because this SLA is not bound to a workflow.
                  </div>
                  <div v-if="selected_sla && !selected_sla.workflow_id" class="text-secondary text-xxs mt-1">
                    Bind a workflow to this SLA first, then status-based triggers can use that workflow's statuses.
                  </div>
                </div>

                <div v-else-if="trigger_form.trigger_type === 'question_answered'" class="col-12">
                  <label class="modal-form-label text-xs" for="sla_section_question_id">Question</label>
                  <select
                    id="sla_section_question_id"
                    v-model="trigger_form.section_question_id"
                    class="form-select bg-light border-0 rounded-3 text-xs"
                    :disabled="context_loading || !question_options.length"
                  >
                    <option value="">Select a question</option>
                    <option v-for="question in question_options" :key="question.id" :value="String(question.id)">
                      {{ question.name }}
                    </option>
                  </select>
                  <div v-if="!context_loading && !question_options.length" class="text-secondary text-xxs mt-1">
                    No questions were found. Pass a `form_id` in the route to load the correct questions.
                  </div>
                </div>

                <div v-else class="col-12">
                  <div class="alert alert-light border mb-0 py-2 text-xs text-secondary rounded-3">
                    No workflow status or section question is required for form submissions.
                  </div>
                </div>

                <div class="col-12 d-none">
                  <label class="modal-form-label text-xs w-100 mb-0">
                    <span class="d-block mb-2">Trigger Condition</span>
                    <span class="d-flex align-items-center justify-content-between rounded-3 border bg-light px-3 py-2">
                      <span class="text-secondary text-xs">Auto start this SLA</span>
                      <input v-model="trigger_form.auto_start" class="form-check-input pm-purple-check ms-2" type="checkbox">
                    </span>
                  </label>
                </div>

                <div class="col-12 d-flex justify-content-end gap-2">
                  <button
                    type="submit"
                    class="btn btn-primary-dark btn-sm rounded-3 text-xs shadow text-dark w-100 py-2"
                    :disabled="trigger_saving || !selected_sla"
                  >
                    <span v-if="trigger_saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ trigger_saving ? 'Saving...' : (trigger_form.id ? 'Update Trigger' : 'Create Trigger') }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section v-else class="entity-inventory-panel rounded-4 border shadow-sm p-5 text-center">
        <div class="fw-bold mb-2">SLA not found</div>
        <div class="text-secondary small mb-3">Return to the SLA list and click Manage on the SLA you want to configure.</div>
        <button type="button" class="btn btn-primary-dark btn-sm rounded-3 text-xs px-3" @click="go_back">
          Back to SLA List
        </button>
      </section>
    </div>
  </div>
</div>
